import React, { useState, useEffect } from 'react';

const SecurityMonitor = () => {
  const [securityLogs, setSecurityLogs] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [threatLevel, setThreatLevel] = useState('low');
  const [activeThreats, setActiveThreats] = useState([]);

  useEffect(() => {
    // Load security logs from localStorage
    const logs = JSON.parse(localStorage.getItem('securityLogs') || '[]');
    setSecurityLogs(logs);
    
    // Count high severity alerts
    const alerts = logs.filter(log => log.severity === 'error' || log.severity === 'warning');
    setAlertCount(alerts.length);
    
    // Calculate threat level
    const errorCount = logs.filter(log => log.severity === 'error').length;
    const warningCount = logs.filter(log => log.severity === 'warning').length;
    
    if (errorCount > 5) {
      setThreatLevel('critical');
    } else if (errorCount > 2 || warningCount > 10) {
      setThreatLevel('high');
    } else if (warningCount > 5) {
      setThreatLevel('medium');
    } else {
      setThreatLevel('low');
    }
    
    // Detect active threats
    const recentLogs = logs.filter(log => {
      const logTime = new Date(log.timestamp).getTime();
      const now = Date.now();
      return (now - logTime) < 300000; // Last 5 minutes
    });
    
    const threats = recentLogs.filter(log => 
      log.severity === 'error' || 
      log.event.includes('INVALID') || 
      log.event.includes('BLOCKED') ||
      log.event.includes('MISMATCH')
    );
    
    setActiveThreats(threats);
    
    // Listen for new security events
    const handleStorageChange = (e) => {
      if (e.key === 'securityLogs') {
        const newLogs = JSON.parse(e.newValue || '[]');
        setSecurityLogs(newLogs);
        
        const alerts = newLogs.filter(log => log.severity === 'error' || log.severity === 'warning');
        setAlertCount(alerts.length);
        
        // Recalculate threat level
        const newErrorCount = newLogs.filter(log => log.severity === 'error').length;
        const newWarningCount = newLogs.filter(log => log.severity === 'warning').length;
        
        if (newErrorCount > 5) {
          setThreatLevel('critical');
        } else if (newErrorCount > 2 || newWarningCount > 10) {
          setThreatLevel('high');
        } else if (newWarningCount > 5) {
          setThreatLevel('medium');
        } else {
          setThreatLevel('low');
        }
        
        // Update active threats
        const recentNewLogs = newLogs.filter(log => {
          const logTime = new Date(log.timestamp).getTime();
          const now = Date.now();
          return (now - logTime) < 300000;
        });
        
        const newThreats = recentNewLogs.filter(log => 
          log.severity === 'error' || 
          log.event.includes('INVALID') || 
          log.event.includes('BLOCKED') ||
          log.event.includes('MISMATCH')
        );
        
        setActiveThreats(newThreats);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const clearLogs = () => {
    localStorage.removeItem('securityLogs');
    setSecurityLogs([]);
    setAlertCount(0);
    setThreatLevel('low');
    setActiveThreats([]);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return 'text-red-600 bg-red-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'info': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getThreatLevelColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const exportLogs = () => {
    const logs = JSON.parse(localStorage.getItem('securityLogs') || '[]');
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security-logs-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isVisible) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsVisible(true)}
          className={`px-4 py-2 rounded-full shadow-lg font-semibold ${
            threatLevel === 'critical' ? 'bg-red-500 text-white hover:bg-red-600' :
            threatLevel === 'high' ? 'bg-orange-500 text-white hover:bg-orange-600' :
            threatLevel === 'medium' ? 'bg-yellow-500 text-white hover:bg-yellow-600' :
            alertCount > 0 ? 'bg-yellow-500 text-white hover:bg-yellow-600' : 
            'bg-green-500 text-white hover:bg-green-600'
          }`}
        >
          🔒 Security {alertCount > 0 && `(${alertCount})`}
          {threatLevel !== 'low' && ` [${threatLevel.toUpperCase()}]`}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold">Security Monitor</h2>
            <div className={`px-3 py-1 rounded-full text-white text-sm font-semibold ${getThreatLevelColor(threatLevel)}`}>
              Threat Level: {threatLevel.toUpperCase()}
            </div>
            {activeThreats.length > 0 && (
              <div className="px-3 py-1 rounded-full bg-red-500 text-white text-sm font-semibold">
                Active Threats: {activeThreats.length}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportLogs}
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Export Logs
            </button>
            <button
              onClick={clearLogs}
              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Clear Logs
            </button>
            <button
              onClick={() => setIsVisible(false)}
              className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">
          {/* Security Statistics */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold mb-3">Security Statistics</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total Events:</span>
                  <span className="font-semibold">{securityLogs.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Errors:</span>
                  <span className="font-semibold text-red-600">
                    {securityLogs.filter(log => log.severity === 'error').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Warnings:</span>
                  <span className="font-semibold text-yellow-600">
                    {securityLogs.filter(log => log.severity === 'warning').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Info:</span>
                  <span className="font-semibold text-blue-600">
                    {securityLogs.filter(log => log.severity === 'info').length}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Active Threats */}
            {activeThreats.length > 0 && (
              <div className="bg-red-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3 text-red-800">Active Threats</h3>
                <div className="space-y-2">
                  {activeThreats.slice(0, 5).map((threat, index) => (
                    <div key={index} className="text-sm p-2 bg-red-100 rounded">
                      <div className="font-semibold text-red-800">{threat.event}</div>
                      <div className="text-red-600">{new Date(threat.timestamp).toLocaleTimeString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Security Logs */}
          <div className="lg:col-span-2">
            <div className="bg-gray-50 rounded-lg p-4 h-[60vh] overflow-y-auto">
              <h3 className="font-semibold mb-3">Security Events</h3>
              {securityLogs.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No security events logged</p>
              ) : (
                <div className="space-y-2">
                  {securityLogs.slice().reverse().map((log, index) => (
                    <div key={index} className={`p-3 rounded border ${getSeverityColor(log.severity)}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{log.event}</span>
                        <span className="text-sm opacity-75">{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="text-sm mt-1">
                        <strong>Severity:</strong> {log.severity}
                      </div>
                      {log.details && (
                        <div className="text-sm mt-1">
                          <strong>Details:</strong> 
                          <pre className="mt-1 text-xs bg-white p-2 rounded overflow-x-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </div>
                      )}
                      <div className="text-xs mt-1 opacity-75">
                        User: {log.userId} | URL: {log.url}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t bg-gray-50">
          <div className="flex justify-between text-sm">
            <span>Total Events: {securityLogs.length}</span>
            <span>Alerts: {alertCount}</span>
            <span>Threat Level: {threatLevel.toUpperCase()}</span>
            <span>Active Threats: {activeThreats.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityMonitor; 